import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Calendar, Eye, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { fmtLongDate } from '../utils/dateUtils';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const markdownComponents = {
  h1: () => null, // Skip h1 in markdown since we render it above
  h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-zinc-900 mt-12 mb-4" {...props} />,
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPost = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
    } catch {
      setError('Blog post not found');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (slug) { fetchPost(); }
  }, [slug, fetchPost]);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | North Texas Welding and Fabrication Blog`;
    }
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-zinc-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-zinc-900 mb-4">Article Not Found</h1>
          <p className="text-zinc-600 mb-8">{error}</p>
          <Button onClick={() => navigate('/blog')} className="bg-red-600 hover:bg-red-700">
            Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  const formattedDate = fmtLongDate(post.generated_at);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <meta name="description" content={post.meta_description} />
        <link rel="canonical" href={`https://ntxpipefence.com/blog/${post.slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.meta_description} />
        <meta property="og:url" content={`https://ntxpipefence.com/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
      </Helmet>
      {/* Header */}
      <header className="bg-black border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Button 
            onClick={() => navigate('/blog')}
            variant="ghost"
            className="text-white hover:text-red-600 mb-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Blog
          </Button>
          
          <div className="flex items-center space-x-4 text-sm text-zinc-400">
            <div className="flex items-center">
              <Calendar size={16} className="mr-2" />
              {formattedDate}
            </div>
            <div className="flex items-center">
              <Eye size={16} className="mr-2" />
              {post.views} views
            </div>
            <div className="flex items-center">
              <MapPin size={16} className="mr-2" />
              {post.service_area}
            </div>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none
          prose-headings:text-zinc-900 prose-headings:font-bold
          prose-h1:text-4xl prose-h1:mb-6 prose-h1:text-red-600
          prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:text-zinc-900
          prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
          prose-p:text-zinc-700 prose-p:leading-relaxed prose-p:mb-6
          prose-a:text-red-600 prose-a:no-underline hover:prose-a:underline
          prose-strong:text-zinc-900 prose-strong:font-semibold
          prose-ul:my-6 prose-ul:list-disc prose-ul:pl-6
          prose-li:text-zinc-700 prose-li:mb-2">
          {/* Extract and render H1 separately for proper SEO */}
          <h1 className="text-4xl font-bold text-red-600 mb-6">{post.title}</h1>
          <ReactMarkdown components={markdownComponents}>
            {post.content}
          </ReactMarkdown>
        </div>

        {/* CTA Section */}
        <div className="mt-16 p-8 bg-zinc-900 rounded-xl text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            Ready to Start Your Project?
          </h3>
          <p className="text-zinc-300 mb-6 max-w-2xl mx-auto">
            Contact North Texas Welding and Fabrication, LLC today for a free consultation 
            on your fencing or welding project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/#contact')}
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white font-semibold"
            >
              Get Free Estimate
            </Button>
            <Button 
              as="a"
              href="tel:8177235006"
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-black font-semibold"
            >
              Call 817-723-5006
            </Button>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-black py-8 border-t border-zinc-800 mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-zinc-400 text-sm">
            © {new Date().getFullYear()} North Texas Welding and Fabrication, LLC
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;
